import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2, Plus, Trash2, RotateCcw } from "lucide-react";

export const ContractDefaultsForm = ({ isOwner }) => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { axios.get(`${API}/contracts/settings`).then(r => setS(r.data)).catch(() => toast.error("Failed to load contract defaults")); }, []);
  const set = (k, v) => setS(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { defaults, ...payload } = s;
      await axios.put(`${API}/contracts/settings`, payload);
      toast.success("Contract defaults saved");
    } catch (e) {
      if (e.response?.status === 202) toast.info("Changes sent to owner for approval");
      else toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!s) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>;
  const pctSum = s.payment_schedule.reduce((a, m) => a + Number(m.percentage || 0), 0);

  return (
    <div className="space-y-5" data-testid="contract-defaults-form">
      {!isOwner && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">Changes are sent to the owner for approval before they take effect.</p>}
      <Card><CardHeader><CardTitle className="text-base">Company header</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Company name</Label><Input value={s.company_name} onChange={e => set("company_name", e.target.value)} data-testid="cd-company" /></div>
          <div><Label className="text-xs">Phone</Label><Input value={s.company_phone} onChange={e => set("company_phone", e.target.value)} data-testid="cd-phone" /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Address lines (one per line)</Label><Textarea rows={2} value={(s.company_address_lines || []).join("\n")} onChange={e => set("company_address_lines", e.target.value.split("\n"))} data-testid="cd-address" /></div>
        </CardContent></Card>

      <Card><CardHeader><CardTitle className="text-base">Default payment schedule</CardTitle><CardDescription>Percentages must total 100. Use <code>{"{pct}"}</code> in a label to insert the percentage.</CardDescription></CardHeader>
        <CardContent className="space-y-2">
          {s.payment_schedule.map((m, i) => (
            <div key={i} className="flex gap-2 items-center">
              <Input value={m.label} onChange={e => set("payment_schedule", s.payment_schedule.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} data-testid={`cd-ps-label-${i}`} />
              <Input type="number" className="w-24 text-right" value={m.percentage} onChange={e => set("payment_schedule", s.payment_schedule.map((x, j) => j === i ? { ...x, percentage: Number(e.target.value) } : x))} data-testid={`cd-ps-pct-${i}`} /><span className="text-sm text-slate-400">%</span>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => set("payment_schedule", s.payment_schedule.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          <div className="flex justify-between items-center">
            <Button size="sm" variant="outline" onClick={() => set("payment_schedule", [...s.payment_schedule, { label: "", percentage: 0 }])}><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
            <span className={`text-xs ${pctSum !== 100 ? "text-red-600 font-medium" : "text-slate-500"}`} data-testid="cd-ps-sum">Total {pctSum}%</span>
          </div>
        </CardContent></Card>

      <Card><CardHeader><CardTitle className="text-base">Clauses</CardTitle><CardDescription>Clause I is fixed wording; clauses III+ are numbered automatically. Placeholders: <code>{"{start_date}"}</code> <code>{"{completion_date}"}</code> <code>{"{work_order_ref}"}</code></CardDescription></CardHeader>
        <CardContent className="space-y-4">
          <div><div className="flex justify-between"><Label className="text-xs">Clause I</Label><ResetBtn onClick={() => set("clause_i", s.defaults.clause_i)} /></div><Textarea rows={4} value={s.clause_i} onChange={e => set("clause_i", e.target.value)} data-testid="cd-clause-i" /></div>
          {s.clauses.map((c, i) => (
            <div key={i} className="border rounded-md p-3 space-y-2">
              <div className="flex gap-2 items-center">
                <span className="text-sm text-slate-500 w-8">{["III", "IV", "V", "VI", "VII", "VIII", "IX", "X"][i]}.</span>
                <Input value={c.title} onChange={e => set("clauses", s.clauses.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} data-testid={`cd-clause-title-${i}`} />
                <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => set("clauses", s.clauses.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
              </div>
              <Textarea rows={4} value={c.body} onChange={e => set("clauses", s.clauses.map((x, j) => j === i ? { ...x, body: e.target.value } : x))} data-testid={`cd-clause-body-${i}`} />
            </div>
          ))}
          <div className="flex justify-between"><Button size="sm" variant="outline" onClick={() => set("clauses", [...s.clauses, { title: "", body: "" }])}><Plus className="w-3.5 h-3.5 mr-1" /> Add clause</Button><ResetBtn onClick={() => set("clauses", s.defaults.clauses)} label="Reset all clauses" /></div>
        </CardContent></Card>

      <div className="grid md:grid-cols-2 gap-5">
        <ListEditor title='Schedule "A" — Contractor responsibilities' items={s.schedule_a} onChange={v => set("schedule_a", v)} onReset={() => set("schedule_a", s.defaults.schedule_a)} testId="cd-sched-a" />
        <ListEditor title='Schedule "B" — Home owner responsibilities' items={s.schedule_b} onChange={v => set("schedule_b", v)} onReset={() => set("schedule_b", s.defaults.schedule_b)} testId="cd-sched-b" />
      </div>

      <Button onClick={save} disabled={saving} data-testid="save-contract-defaults">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{isOwner ? "Save Contract Defaults" : "Submit for Approval"}</Button>
    </div>
  );
};

const ResetBtn = ({ onClick, label = "Reset to default" }) => (
  <button type="button" onClick={onClick} className="text-xs text-slate-400 hover:text-indigo-600 flex items-center gap-1"><RotateCcw className="w-3 h-3" /> {label}</button>
);

export const ListEditor = ({ title, items, onChange, onReset, testId }) => (
  <Card><CardHeader><div className="flex justify-between items-center"><CardTitle className="text-base">{title}</CardTitle>{onReset && <ResetBtn onClick={onReset} />}</div></CardHeader>
    <CardContent className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex gap-2 items-start">
          <span className="text-slate-400 mt-2">•</span>
          <Textarea rows={2} value={it} onChange={e => onChange(items.map((x, j) => j === i ? e.target.value : x))} data-testid={`${testId}-${i}`} />
          <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600 mt-1" onClick={() => onChange(items.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={() => onChange([...items, ""])} data-testid={`${testId}-add`}><Plus className="w-3.5 h-3.5 mr-1" /> Add item</Button>
    </CardContent></Card>
);
